import { useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useLazyGetFormilyBlockQuery, useMediaQuery } from '@segway/core'

import { Close, CloseOpenIcon, DirectionIcon, PageBuilderRenderer } from '@/components'
import { Link } from '@/i18n/navigation'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/ui'

import { useProduct, useProductData } from '../../ProductProvider'

interface BlockData {
  title: string
  items: {
    title: string
    url: string
  }[]
}

const SpecificationsSupport = () => {
  const { isEbike } = useProduct()
  const product = useProductData()
  const responsive = useMediaQuery()
  const [specificationsVisible, setSpecificationsVisible] = useState(false)
  const [blockData, setBlockData] = useState<BlockData | null>(null)

  const [getBlockData] = useLazyGetFormilyBlockQuery()
  const t = useTranslations('Product')

  const blockParams = useMemo(() => {
    if (isEbike) {
      return { identify: 'pdp_shipping_return_ebike' }
    } else {
      return { identify: 'pdp_shipping_return_catalog' }
    }
  }, [isEbike])

  useEffect(() => {
    if (blockParams) {
      getBlockData(blockParams)
        .unwrap()
        .then((res) => {
          const data = res.formilyBlock?.block_data
          if (!!data) {
            setBlockData(JSON.parse(data))
          }
        })
        .catch((error) => {
          console.log(error)
        })
    }
  }, [blockParams, getBlockData])

  const specifications = useMemo(() => {
    return product?.specifications?.html ?? ''
  }, [product?.specifications?.html])

  const download = useMemo(() => {
    return product?.download?.html ?? ''
  }, [product?.download?.html])

  /**
   * QA 内容
   */
  const qaContent: { answer: string; question: string }[] = useMemo(() => {
    const data = product?.formily_content?.find((item) => item?.code === 'faq')

    if (data) {
      return JSON.parse(data.data || '') || []
    }

    return []
  }, [product])

  const isSupportShow = useMemo(() => {
    return specifications || download || blockData
  }, [blockData, download, specifications])

  return (
    <>
      {isSupportShow && (
        <div className="flex flex-col justify-between xl:flex-row">
          <div className="mb-7 w-full xl:mb-0 xl:max-w-[500px]">
            <h3 className="mb-[10px] text-wrap font-miSansSemiBold520 text-[18px] text-black xl:mb-10 xl:text-[48px]">
              {t('product_download')}
            </h3>
            <span className="text-wrap font-miSansMedium380 text-base leading-[1.2] text-gray-6 xl:text-[20px] xl:leading-[1.6]">
              {t('product_download_details')}
            </span>
          </div>
          <div className="w-full xl:w-[45%]">
            <Accordion type="single" collapsible className="w-full">
              {blockData && (
                <AccordionItem
                  value="item-1"
                  className="group border-b-black [&_.arrow]:hidden [&_svg]:hidden">
                  <AccordionTrigger className="flex justify-between py-6 xl:py-[31px]">
                    <span className="font-miSansDemiBold450 text-lg text-black group-data-[state=open]:text-primary xl:text-[32px]">
                      {t('free_shipping')}
                    </span>
                    {responsive && responsive.xl && (
                      <>
                        <Close width={42} height={42} className="group-data-[state=closed]:block" />
                        <CloseOpenIcon
                          width={42}
                          height={42}
                          className="group-data-[state=open]:block group-data-[state=open]:text-primary"
                        />
                      </>
                    )}
                    {responsive && !responsive.xl && (
                      <>
                        <Close width={20} height={20} className="group-data-[state=closed]:block" />
                        <CloseOpenIcon
                          width={20}
                          height={20}
                          className="group-data-[state=open]:block group-data-[state=open]:text-primary"
                        />
                      </>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="font-miSansMedium380 text-sm text-gray-6 xl:text-[16px]">
                      {blockData?.title}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-6 xl:mt-[12px]">
                      {!!blockData?.items?.length &&
                        blockData?.items.map((item, index) => (
                          <Link href={item.url} key={index}>
                            <span className="cursor-pointer font-miSansDemiBold450 text-sm text-gray-8 underline xl:text-base">
                              {item.title}
                            </span>
                          </Link>
                        ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
              {download && (
                <AccordionItem
                  value="item-2"
                  className="group border-b-black [&_.arrow]:hidden [&_svg]:hidden">
                  <AccordionTrigger className="flex justify-between py-6 xl:py-9">
                    <span className="text-left font-miSansDemiBold450 text-lg text-black group-data-[state=open]:text-primary xl:text-start xl:text-[32px]">
                      {t('user_manual')}
                    </span>
                    {responsive && responsive.xl && (
                      <>
                        <Close width={42} height={42} className="group-data-[state=closed]:block" />
                        <CloseOpenIcon
                          width={42}
                          height={42}
                          className="group-data-[state=open]:block group-data-[state=open]:text-primary"
                        />
                      </>
                    )}
                    {responsive && !responsive.xl && (
                      <>
                        <Close width={20} height={20} className="group-data-[state=closed]:block" />
                        <CloseOpenIcon
                          width={20}
                          height={20}
                          className="group-data-[state=open]:block group-data-[state=open]:text-primary"
                        />
                      </>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className="pdp-support-user-materials pb-6">
                    {download && <PageBuilderRenderer content={download} />}
                  </AccordionContent>
                </AccordionItem>
              )}
              {specifications && (
                <AccordionItem
                  value="item-3"
                  className="group border-b-black [&_.arrow]:hidden [&_svg]:hidden">
                  <div
                    className="flex cursor-pointer justify-between py-6 xl:py-9"
                    onClick={(e) => {
                      e.preventDefault()
                      setSpecificationsVisible(true)
                    }}>
                    <span className="font-miSansDemiBold450 text-lg text-black xl:text-[32px]">
                      {t('view_specifications')}
                    </span>
                    <span>
                      {responsive && responsive.xl && (
                        <DirectionIcon
                          width={42}
                          height={42}
                          className="group-data-[state=closed]:block"
                        />
                      )}
                      {responsive && !responsive.xl && (
                        <DirectionIcon
                          width={20}
                          height={20}
                          className="group-data-[state=closed]:block"
                        />
                      )}
                    </span>
                  </div>
                </AccordionItem>
              )}
              {qaContent.length > 0 && (
                <AccordionItem
                  value="item-4"
                  className="group border-b-black [&_.arrow]:hidden [&_svg]:hidden">
                  <AccordionTrigger className="flex justify-between py-6 xl:py-[31px]">
                    <span className="font-miSansDemiBold450 text-lg text-black group-data-[state=open]:text-primary xl:text-[32px]">
                      Q & A ({qaContent.length})
                    </span>
                    {responsive && responsive.xl && (
                      <>
                        <Close width={42} height={42} className="group-data-[state=closed]:block" />
                        <CloseOpenIcon
                          width={42}
                          height={42}
                          className="group-data-[state=open]:block group-data-[state=open]:text-primary"
                        />
                      </>
                    )}
                    {responsive && !responsive.xl && (
                      <>
                        <Close width={20} height={20} className="group-data-[state=closed]:block" />
                        <CloseOpenIcon
                          width={20}
                          height={20}
                          className="group-data-[state=open]:block group-data-[state=open]:text-primary"
                        />
                      </>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="space-y-3 xl:space-y-7">
                      {qaContent.map((item, index) => {
                        return (
                          <div key={index}>
                            <p className="font-miSansDemiBold450 text-base text-black xl:text-2xl">
                              {item.question}
                            </p>
                            <p
                              className="head mt-2 font-miSansMedium380 text-sm leading-[1.5] text-gray-6 xl:mt-3 xl:text-lg [&_a]:text-black [&_a]:underline"
                              dangerouslySetInnerHTML={{ __html: item.answer }}></p>
                          </div>
                        )
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </div>
          <Dialog open={specificationsVisible} onOpenChange={setSpecificationsVisible}>
            <DialogContent className="max-h-[500px] overflow-hidden px-[10px] xl:!max-w-[960px]">
              <DialogHeader>
                <DialogTitle>{t('view_specifications')}</DialogTitle>
              </DialogHeader>
              <div className="specifications-content max-h-[380px] overflow-y-auto">
                {specifications && <PageBuilderRenderer content={specifications} />}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </>
  )
}

export default SpecificationsSupport
